const _base_spacing_unit = 4;
const _fontFamily = `-apple-system,BlinkMacSystemFont,"Inter","Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans","Liberation Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"`;
const _typography = {
  displayXL: {fontSize: "60px", lineHeight: "72px", letterSpacing: "-0.02em"},
  displayLG: {fontSize: "48px", lineHeight: "60px", letterSpacing: "-0.02em"},
  displayMD: {fontSize: "36px", lineHeight: "44px", letterSpacing: "-0.02em"},
  displaySM: {fontSize: "30px", lineHeight: "38px"},
  displayXS: {fontSize: "24px", lineHeight: "32px"},
  textXL: {fontSize: "20px", lineHeight: "30px"},
  textLG: {fontSize: "18px", lineHeight: "28px"},
  textMD: {fontSize: "16px", lineHeight: "24px"},
  textSM: {fontSize: "14px", lineHeight: "20px"},
  textXS: {fontSize: "12px", lineHeight: "18px"},
  textXXS: {fontSize: "10px", lineHeight: "16px"},
};

const _createTypographyVariant = (baseStyles) => {
  return {
    regular: {...baseStyles, fontWeight: 400, fontFamily: _fontFamily},
    medium: {...baseStyles, fontWeight: 500, fontFamily: _fontFamily},
    semibold: {...baseStyles, fontWeight: 600, fontFamily: _fontFamily},
    bold: {...baseStyles, fontWeight: 700, fontFamily: _fontFamily},
  };
};

const _getSpacing = (...values) => {
  if (values.length === 0) {
    return "0px";
  }
  return values
    .slice(0, 4)
    .map((value) => `${value * _base_spacing_unit}px`)
    .join(" ");
};

const darkTheme = {
  zIndex: {
    dropdown: 1000,
    drawer: 1100,
    modal: 1200,
    tooltip: 1300,
  },
  radius: {
    xs: 2,
    sm: 4,
    md: 6,
    lg: 8,
    xl: 10,
    xxl: 12,
  },
  breakpoints: {
    xs: 0,
    sm: 1024,
    md: 1440,
    lg: 1920,
  },
  spacing: (...val) => _getSpacing(...val),
  typography: {
    displayXL: _createTypographyVariant(_typography.displayXL),
    displayLG: _createTypographyVariant(_typography.displayLG),
    displayMD: _createTypographyVariant(_typography.displayMD),
    displaySM: _createTypographyVariant(_typography.displaySM),
    displayXS: _createTypographyVariant(_typography.displayXS),
    textXL: _createTypographyVariant(_typography.textXL),
    textLG: _createTypographyVariant(_typography.textLG),
    textMD: _createTypographyVariant(_typography.textMD),
    textSM: _createTypographyVariant(_typography.textSM),
    textXS: _createTypographyVariant(_typography.textXS),
    textXXS: _createTypographyVariant(_typography.textXXS),
  },
  shadows: {
    default: "0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
    xs: "0px 1px 2px 0px rgba(0, 0, 0, 0.4)",
    sm: "0px 1px 3px 0px rgba(0, 0, 0, 0.5)",
    md: "0px 4px 8px -2px rgba(0, 0, 0, 0.5), 0px 2px 4px -2px rgba(0, 0, 0, 0.4)",
    lg: "0px 12px 16px -4px rgba(0, 0, 0, 0.5), 0px 4px 6px -2px rgba(0, 0, 0, 0.4)",
    xl: "0px 20px 24px -4px rgba(0, 0, 0, 0.5), 0px 8px 8px -4px rgba(0, 0, 0, 0.4)",
  },
  colors: {
    // General
    white: "#1c1e24",
    black: "#000000",
    border: "#3a3e46",
    divider: "#3a3e46",
    textPrimary: "#f5f5f5",
    textSecondary: "#b0b3b8",
    textTertiary: "#717680",
    hoverGray: "#2c2f36",
    inputBG: "#2c2f36",
    // Keywords
    inquiry: "purple",
    pending: "bluegray",
    confirmed: "success",
    declined: "error",
    checked_in: "primary",
    checked_out: "orange",
    cancelled: "warning",
    expired_guest: "indigo",
    expired_host: "rose",
    pending_guest: "bluelight",
    // Gray
    gray: "#717680",
    gray25: "#202226",
    gray50: "#1c1e24",
    gray100: "#2c2f36",
    gray200: "#3a3e46",
    gray300: "#4d5057",
    gray400: "#717680",
    gray500: "#a4a7ae",
    gray600: "#c0c2c7",
    gray700: "#d5d7da",
    gray800: "#e9eaeb",
    gray900: "#f5f5f5",
    // Primary
    primary: "#4a8bfc",
    primary25: "#12182b",
    primary50: "#172040",
    primary100: "#1c2954",
    primary200: "#243573",
    primary300: "#2d4292",
    primary400: "#3b5fc5",
    primary500: "#4a8bfc",
    primary600: "#6098fd",
    primary700: "#7aacfd",
    primary800: "#a7c7fd",
    primary900: "#e3edff",
    // Error
    error: "#f04438",
    error25: "#2a1414",
    error50: "#361616",
    error100: "#471a1a",
    error200: "#5c2121",
    error300: "#7d2b2b",
    error400: "#a13636",
    error500: "#f04438",
    error600: "#f56358",
    error700: "#f88078",
    error800: "#fb9d98",
    error900: "#fee4e2",
    // Warning
    warning: "#f79009",
    warning25: "#281b06",
    warning50: "#392508",
    warning100: "#4e3310",
    warning200: "#704a12",
    warning300: "#946118",
    warning400: "#b57a1e",
    warning500: "#f79009",
    warning600: "#faa530",
    warning700: "#fcb954",
    warning800: "#fdcd78",
    warning900: "#fef0c7",
    // Success
    success: "#12b76a",
    success25: "#0a2718",
    success50: "#0e331f",
    success100: "#114328",
    success200: "#175937",
    success300: "#1e7049",
    success400: "#259d64",
    success500: "#12b76a",
    success600: "#32d583",
    success700: "#6ce9a6",
    success800: "#a6f4c5",
    success900: "#d1fadf",
    // Blue
    blue: "#2e90fa",
    blue25: "#101b2b",
    blue50: "#142236",
    blue100: "#1c2d47",
    blue200: "#273c5f",
    blue300: "#324c7c",
    blue400: "#3d5da0",
    blue500: "#2e90fa",
    blue600: "#53b1fd",
    blue700: "#84caff",
    blue800: "#b2ddff",
    blue900: "#d1e9ff",
    // Bluegray
    bluegray: "#4e5ba6",
    bluegray25: "#101223",
    bluegray50: "#1b1f3d",
    bluegray100: "#293056",
    bluegray200: "#363f72",
    bluegray300: "#3e4784",
    bluegray400: "#4e5ba6",
    bluegray500: "#717bbc",
    bluegray600: "#8f96c9",
    bluegray700: "#afb5d9",
    bluegray800: "#d5d9eb",
    bluegray900: "#f8f9fc",
    // Bluelight
    bluelight: "#0ba5ec",
    bluelight25: "#0b1b25",
    bluelight50: "#0f2636",
    bluelight100: "#153949",
    bluelight200: "#1c4d62",
    bluelight300: "#25627d",
    bluelight400: "#2f7ea1",
    bluelight500: "#0ba5ec",
    bluelight600: "#36bffa",
    bluelight700: "#7cd4fd",
    bluelight800: "#b9e6fe",
    bluelight900: "#e0f2fe",
    // Indigo
    indigo: "#6172f3",
    indigo25: "#151828",
    indigo50: "#1c203a",
    indigo100: "#242950",
    indigo200: "#2d3169",
    indigo300: "#3538cd",
    indigo400: "#444ce7",
    indigo500: "#6172f3",
    indigo600: "#8098f9",
    indigo700: "#a4bcfd",
    indigo800: "#c7d7fe",
    indigo900: "#eef4ff",
    // Purple
    purple: "#7a5af8",
    purple25: "#191528",
    purple50: "#221b39",
    purple100: "#2d2350",
    purple200: "#3e1c96",
    purple300: "#4a1fb8",
    purple400: "#5925dc",
    purple500: "#7a5af8",
    purple600: "#9b8afb",
    purple700: "#bdb4fe",
    purple800: "#d9d6fe",
    purple900: "#f4f3ff",
    // Pink
    pink: "#ee46bc",
    pink25: "#271426",
    pink50: "#341a32",
    pink100: "#461c42",
    pink200: "#5e2158",
    pink300: "#851651",
    pink400: "#c11574",
    pink500: "#ee46bc",
    pink600: "#f670c7",
    pink700: "#faa7e0",
    pink800: "#fcceee",
    pink900: "#fdf2fa",
    // Rosé
    rose: "#f63d68",
    rose25: "#270e18",
    rose50: "#34101f",
    rose100: "#471429",
    rose200: "#5e1936",
    rose300: "#89123e",
    rose400: "#c01048",
    rose500: "#f63d68",
    rose600: "#fd6f8e",
    rose700: "#fea3b4",
    rose800: "#fecdd6",
    rose900: "#fff1f3",
    // Orange
    orange: "#fb6514",
    orange25: "#271507",
    orange50: "#331a09",
    orange100: "#47220d",
    orange200: "#5f2c12",
    orange300: "#7e2410",
    orange400: "#c4320a",
    orange500: "#fb6514",
    orange600: "#fd853a",
    orange700: "#feb273",
    orange800: "#fddcab",
    orange900: "#fff6ed",
  },
};

export default darkTheme;
